import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { getStateParkPhoto, getPlaceDetails } from '../../../services/googleMapsPlaceService';
import { supabase } from '../../../lib/supabase';
import { trackButtonClick, trackTripAction } from '../../../utils/googleAnalyticsService';

const TripCard = ({ trip, onView, onEdit, onDelete }) => {
  const [parkImage, setParkImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [parkState, setParkState] = useState(null);

  // ✅ NEW: Clean park name by removing common suffixes
  const getCleanParkName = (parkName) => {
    if (!parkName) return '';
    
    // Remove common suffixes
    const suffixesToRemove = [
      ' State Park',
      ' State Park and Wildlife Refuge',
      ' National Park',
      ' Wildlife Refuge',
      ' State Recreation Area',
      ' State Natural Area'
    ];
    
    let cleanName = parkName;
    for (const suffix of suffixesToRemove) {
      if (cleanName?.endsWith(suffix)) {
        cleanName = cleanName?.slice(0, -suffix?.length);
        break;
      }
    }
    
    return cleanName;
  };

  // ✅ FIXED: Use correct database field mapping (place_id → placeId)
  const tripPlaceId = trip?.place_id || trip?.placeId; // Support both naming conventions

  // ✅ FIXED: Extract state from park name with improved logic for multiple parks
  const extractStateFromParkName = (parkName) => {
    if (!parkName) return null;

    console.log('🔍 Extracting state from park name:', parkName);

    // Strategy 1: Check if there's a comma-separated location at the end
    // Example: "Yellowstone National Park, Wyoming"
    if (parkName?.includes(',')) {
      const parts = parkName?.split(',')?.map(part => part?.trim());
      const lastPart = parts?.[parts?.length - 1];
      
      console.log('📍 Comma-separated parts:', parts);
      console.log('📍 Last part:', lastPart);
      
      // Validate if it looks like a state name
      const parkSuffixes = ['State Park', 'National Park', 'Wildlife Refuge', 'State Recreation Area', 'State Natural Area'];
      const isParkSuffix = parkSuffixes?.some(suffix => 
        lastPart?.toLowerCase()?.includes(suffix?.toLowerCase())
      );
      
      // If last part is not a park suffix and looks like a state name
      if (lastPart && 
          !isParkSuffix &&
          lastPart?.length >= 2 && 
          lastPart?.length <= 50 &&
          /^[A-Z][a-zA-Z\s]*$/?.test(lastPart)) {
        console.log('✅ Valid state extracted from comma-separated format:', lastPart);
        return lastPart;
      }
    }

    // Strategy 2: For multiple parks, use the FIRST park name for state lookup
    // Example: "Gateway Arch National Park, Elephant Rocks State Park" → use "Gateway Arch National Park"
    let primaryPark = parkName;
    if (parkName?.includes(',')) {
      const firstPark = parkName?.split(',')?.[0]?.trim();
      console.log('🎯 Multiple parks detected, using first park for lookup:', firstPark);
      primaryPark = firstPark;
    }

    // Strategy 3: Try to infer state from common park name patterns
    // Examples: "Yellowstone National Park" → Wyoming, "Great Smoky Mountains National Park" → Tennessee/North Carolina
    const stateInferences = {
      'yellowstone': 'Wyoming',
      'yosemite': 'California',
      'grand canyon': 'Arizona',
      'zion': 'Utah',
      'glacier': 'Montana',
      'acadia': 'Maine',
      'everglades': 'Florida',
      'joshua tree': 'California',
      'arches': 'Utah',
      'great smoky mountains': 'Tennessee',
      'rocky mountain': 'Colorado',
      'shenandoah': 'Virginia',
      'big bend': 'Texas',
      'denali': 'Alaska',
      'mount rainier': 'Washington',
      'olympic': 'Washington',
      'sequoia': 'California',
      'death valley': 'California',
      'gateway arch': 'Missouri',
      'mammoth cave': 'Kentucky',
      'carlsbad caverns': 'New Mexico',
      'crater lake': 'Oregon',
      'redwood': 'California',
      'badlands': 'South Dakota',
      'canyonlands': 'Utah',
      'capitol reef': 'Utah',
      'bryce canyon': 'Utah',
      'grand teton': 'Wyoming',
      'mesa verde': 'Colorado',
      'petrified forest': 'Arizona',
      'wind cave': 'South Dakota',
      'haleakalā': 'Hawaii',
      'hawaiʻi volcanoes': 'Hawaii',
      'north cascades': 'Washington',
      'saguaro': 'Arizona',
      'black canyon': 'Colorado',
      'great basin': 'Nevada',
      'guadalupe mountains': 'Texas',
      'pinnacles': 'California',
      'congaree': 'South Carolina',
      'cuyahoga valley': 'Ohio',
      'hot springs': 'Arkansas',
      'indiana dunes': 'Indiana',
      'isle royale': 'Michigan',
      'theodore roosevelt': 'North Dakota',
      'virgin islands': 'U.S. Virgin Islands',
      'voyageurs': 'Minnesota',
      'white sands': 'New Mexico'
    };

    const lowerPark = primaryPark?.toLowerCase();
    for (const [parkKey, state] of Object.entries(stateInferences)) {
      if (lowerPark?.includes(parkKey)) {
        console.log(`✅ Inferred state from park name pattern "${parkKey}":`, state);
        return state;
      }
    }

    console.warn('⚠️ Could not extract state from park name:', parkName);
    return null;
  };

  // ✅ COMPLETELY REWRITTEN: Use Google Place ID to fetch state information with improved fallbacks
  useEffect(() => {
    const fetchParkState = async () => {
      if (!trip?.park) {
        console.log('⚠️ TripCard - No park name available for trip:', trip?.id);
        return;
      }

      console.log('🔍 TripCard - Fetching state for park:', trip?.park, '| Place ID:', tripPlaceId);

      try {
        // Strategy 1: If trip has place_id, use it to get accurate state from Google Maps
        if (tripPlaceId) {
          console.log('🗺️ TripCard - Using Place ID to fetch state:', tripPlaceId);
          const placeDetails = await getPlaceDetails(tripPlaceId);
          
          if (placeDetails?.state) {
            setParkState(placeDetails?.state);
            console.log('✅ TripCard - Fetched state from Google Place ID:', tripPlaceId, '→', placeDetails?.state);
            return; // Exit early - we got the state from Google Maps
          } else {
            console.warn('⚠️ TripCard - Place ID exists but no state returned:', tripPlaceId);
          }
        } else {
          console.log('⚠️ TripCard - No Place ID available for trip:', trip?.id);
        }

        // ✅ NEW: For multiple parks, use the first park for database lookup
        let primaryPark = trip?.park;
        if (trip?.park?.includes(',')) {
          primaryPark = trip?.park?.split(',')?.[0]?.trim();
          console.log('🎯 Multiple parks detected, using first park for database lookup:', primaryPark);
        }

        // Strategy 2: Fallback to Supabase database lookup (exact match on primary park)
        let { data, error } = await supabase
          ?.from('state_parks')
          ?.select('state')
          ?.eq('name', primaryPark)
          ?.single();

        if (error && error?.code !== 'PGRST116') {
          console.error('❌ TripCard - Supabase error:', error);
        }

        // Strategy 3: Try partial match in database if exact match failed
        if (!data?.state) {
          const parkNameWithoutComma = primaryPark?.split(',')?.[0]?.trim();
          console.log('🔍 TripCard - Trying partial match for:', parkNameWithoutComma);
          
          ({ data, error } = await supabase
            ?.from('state_parks')
            ?.select('state')
            ?.ilike('name', `%${parkNameWithoutComma}%`)
            ?.limit(1)
            ?.single());

          if (data?.state) {
            console.log('✅ TripCard - Found state via partial match:', parkNameWithoutComma, '→', data?.state);
          }
        }

        // Use database state if found
        if (data?.state) {
          setParkState(data?.state);
          console.log('✅ TripCard - Found state in database for park:', primaryPark, '→', data?.state);
          return;
        }

        // ✅ IMPROVED: Strategy 4 - Extract state from park name string with enhanced logic
        const extractedState = extractStateFromParkName(trip?.park);
        if (extractedState) {
          setParkState(extractedState);
          console.log('✅ TripCard - Extracted state from park name as fallback:', trip?.park, '→', extractedState);
          return;
        }

        console.warn('⚠️ TripCard - Could not determine state for park:', trip?.park, 
          '| Tried all strategies (Place ID, database exact, database partial, string parsing, inference)');
      } catch (error) {
        console.error('❌ TripCard - Error fetching park state:', error);
      }
    };

    fetchParkState();
  }, [trip?.park, tripPlaceId]);

  // ✅ FIXED: Fetch Google Maps photo and properly manage loading state
  useEffect(() => {
    const fetchParkImage = async () => {
      if (!trip?.park && !trip?.destination) {
        setImageLoading(false);
        return;
      }

      try {
        setImageLoading(true);
        
        // Use park name first, fallback to destination
        const searchQuery = trip?.park || trip?.destination;
        
        console.log('🖼️ Fetching park image for:', searchQuery);
        const photoData = await getStateParkPhoto(searchQuery);
        
        if (photoData?.url) {
          setParkImage({
            url: photoData?.url,
            alt: photoData?.alt || `Scenic view of ${searchQuery}`
          });
          console.log('✅ Park image loaded:', photoData?.url);
        } else {
          console.warn('⚠️ No image found for park:', searchQuery);
        }
      } catch (error) {
        console.error('❌ Error fetching park image:', error);
      } finally {
        // ✅ CRITICAL FIX: Set loading to false after fetch completes (success or failure)
        setImageLoading(false);
      }
    };

    fetchParkImage();
  }, [trip?.park, trip?.destination]);

  // ✅ UPDATED: Calculate dynamic status with in-progress detection
  const calculateDynamicStatus = () => {
    if (!trip?.startDate || !trip?.endDate) {
      return trip?.status || 'draft';
    }

    const currentDate = new Date();
    currentDate?.setHours(0, 0, 0, 0);
    
    const startDate = new Date(trip.startDate);
    startDate?.setHours(0, 0, 0, 0);
    
    const endDate = new Date(trip.endDate);
    // ✅ NEW: Set end date to 11:59pm for accurate completion detection
    endDate?.setHours(23, 59, 59, 999);
    
    // ✅ NEW: Check if trip is currently in progress (today is between start and end date)
    if (currentDate >= startDate && currentDate <= endDate) {
      return 'in-progress';
    }
    
    // If trip end date has passed (after 11:59pm)
    if (currentDate > endDate) {
      return 'completed';
    }
    
    // Calculate difference in days for upcoming trips
    const diffTime = startDate?.getTime() - currentDate?.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // If trip is within next 14 days
    if (diffDays >= 0 && diffDays <= 14) {
      return 'upcoming';
    }
    
    // If trip is 14+ days away
    if (diffDays > 14) {
      return 'planning';
    }
    
    // Default to the database status
    return trip?.status || 'draft';
  };

  const dynamicStatus = calculateDynamicStatus();

  // ✅ UPDATED: Add in-progress status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'in-progress':
        return 'bg-orange-500 text-white';
      case 'upcoming':
        return 'bg-success text-success-foreground';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      case 'planning':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // ✅ UPDATED: Add in-progress status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'in-progress':
        return 'Play';
      case 'upcoming':
        return 'Calendar';
      case 'completed':
        return 'CheckCircle';
      case 'planning':
        return 'Clock';
      case 'draft':
        return 'Edit';
      default:
        return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ✅ UPDATED: Use fetched park image with proper fallback logic
  const displayImageUrl = parkImage?.url || trip?.imageUrl || '/assets/images/no_image.png';
  const displayImageAlt = parkImage?.alt || trip?.imageAlt || `Trip to ${trip?.destination || trip?.park}`;

  const handleView = () => {
    trackButtonClick('view_trip', 'View Trip Details', {
      trip_id: trip?.id,
      trip_title: trip?.title,
    });
    trackTripAction('view', trip?.id);
    
    onView(trip);
  };

  const handleEdit = () => {
    trackButtonClick('edit_trip_dashboard', 'Edit Trip', {
      trip_id: trip?.id,
    });
    trackTripAction('edit', trip?.id, {
      source: 'dashboard',
    });
    
    onEdit(trip);
  };

  const handleDelete = () => {
    trackButtonClick('delete_trip', 'Delete Trip', {
      trip_id: trip?.id,
    });
    trackTripAction('delete', trip?.id);
    
    onDelete(trip);
  };

  return (
    <div className="bg-card border border-border rounded-organic shadow-organic hover:shadow-organic-md transition-organic group">
      <div className="relative overflow-hidden rounded-t-organic">
        {imageLoading && (
          <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center z-10">
            <Icon name="Loader" size={32} className="text-muted-foreground animate-spin" />
          </div>
        )}
        <Image
          src={displayImageUrl}
          alt={displayImageAlt}
          className="w-full h-48 object-cover group-hover:scale-105 transition-organic"
        />
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-body font-body-medium ${getStatusColor(dynamicStatus)}`}>
            <Icon name={getStatusIcon(dynamicStatus)} size={12} className="mr-1" />
            {/* ✅ UPDATED: Display "In-Progress" for in-progress status */}
            {dynamicStatus === 'in-progress' ? 'In-Progress' : dynamicStatus?.charAt(0)?.toUpperCase() + dynamicStatus?.slice(1)}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-heading font-heading-bold text-lg text-card-foreground mb-1 line-clamp-1">
              {trip?.title || trip?.destination}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <p className="font-body text-sm text-muted-foreground">
                <span className="font-body-medium text-foreground">
                  {getCleanParkName(trip?.park)}
                </span>
                {parkState && (
                  <>
                    <span className="mx-1.5">•</span>
                    <span className="text-blue-600 font-body-medium">
                      {parkState}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Icon name="Calendar" size={14} className="mr-1" />
              <span>{formatDate(trip?.startDate)} - {formatDate(trip?.endDate)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name="MapPin" size={14} className="mr-1" />
              <span>{trip?.duration} days</span>
            </div>
            <div className="flex items-center">
              <Icon name="Users" size={14} className="mr-1" />
              <span>{trip?.travelers} travelers</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Link to={`/trip-detail-view/${trip?.id}`} className="flex-1 mr-3">
            <Button 
              variant="primary" 
              size="md"
              iconName="Eye"
              className="w-full font-body-semibold shadow-sm hover:shadow-md transition-all bg-[#2D5A27] hover:bg-[#234519] text-white"
            >
              View Details
            </Button>
          </Link>

          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              onClick={handleEdit}
              className="p-2"
            />
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              onClick={handleDelete}
              className="p-2 text-error hover:text-error"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripCard;